import { forEach } from "lodash"
import { useAppStore } from "@/stores/app"

/**
 * Handles errors from API responses.
 *
 * @param status  The HTTP status code.
 * @param message The error message.
 * @param errors  Any validation errors.
 * @param notify  Whether to show a notification.
 * @param loop    Whether to show all errors or just the first error per field.
 *
 * @returns True if the error was handled, false otherwise.
 */
const errorHandler = (
  status: number,
  message = "Unknown Error",
  errors: boolean | any = false,
  notify = true,
  loop = false
): boolean => {
  if (status > 204) {
    if (message === "canceled") return true
    if (notify) {
      if (errors) {
        if (!loop) {
          // Filter errors to only include first message for each field
          errors = Object.entries(errors).reduce((singleError: any, [key, value]) => {
            singleError[key] = Array.isArray(value) ? [value[0]] : value
            return singleError
          }, {})
        }
        loopErrors(errors)
      } else {
        // Handle specific HTTP status codes with appropriate messages
        let errorMessage = message
        if (!message || message === "Unknown Error") {
          switch (status) {
            case 413:
              errorMessage = "File is too large. Please upload a file less than 20MB."
              break
            case 422:
              errorMessage = "Validation failed. Please check your input."
              break
            case 500:
              errorMessage = "Server error. Please try again later."
              break
            default:
              errorMessage = `Request failed with status ${status}`
          }
        }
        useAppStore().addError(errorMessage)
      }
    }
    return true
  }
  return false
}

function loopErrors(errors: any, internal_key: string | null = null) {
  forEach(errors, (value: string | object, key: string) => {
    const field = internal_key ? `${internal_key}.${key}` : key
    if (typeof value === "object") {
      return loopErrors(value, field)
    }

    const field_name = field
      ? field
        .replace(/_/g, ' ')
        .replace(/\./g, ' ')
        .replace(/\w\S*/g, (txt: string) => {
          return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
        })
      : 'Error'
    const showFieldName =  key != '0'

    useAppStore().addError(`${showFieldName ? field_name + ' - ' : ''}${value}`)
  })
}

export default errorHandler
